import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';

import { SiteConfig, TabConfig, TabConfigByName } from './config';

import './app.global.css';
import './grid.global.css';
import './search.global.css';

import { InstantSearch, Configure, connectSearchBox, connectStateResults } from 'react-instantsearch-dom';
import { SearchState } from 'react-instantsearch-core';
import algoliasearch from 'algoliasearch/lite';

import allyTabFocus from 'ally.js/src/maintain/tab-focus';
import allyWhenKey from 'ally.js/src/when/key';

import {
    getFiltersFromQueryString,
    getQueryParameter,
    setNewTabInQueryString,
    setQueryParameters,
    updateFiltersInQueryString,
} from './utils/urlUtils';
import { trackPageCall } from './utils/segmentTracking';

import { Hits } from './components/algolia/hits';
import Filters from './components/filters';
import Navigation from './components/navigation';
import { Pagination } from './components/algolia/pagination';
import SortBy from './components/algolia/sort_by';
import Stats from './components/algolia/stats';
import { ClearRefinements } from './components/algolia/clear_refinements';

import { loadingIcon } from './shared/icons';

// TODO: move show/hide filter code into its own component
import { Label } from './atoms';
import Filter from './static/images/filter.svg';
import Close from './static/images/close.svg';

// Get initial app state from query string
const searchQuery: string = getQueryParameter('query');
const initialTab: string = (() => {
    let tab = getQueryParameter('tab');
    tab = tab?.toLowerCase();
    // If we receive a bad tab name or no tab name,
    // default to first tab
    if (tab && TabConfigByName.hasOwnProperty(tab)) {
        return tab;
    }
    return TabConfig[0].name;
})();
const initialPage: number = (() => {
    const page = parseInt(getQueryParameter('p'));
    if (page && page > 1) {
        return page;
    }
})();
const initialSortBy: string = (() => {
    const sortBy = getQueryParameter('sortBy');
    if (sortBy) {
        return sortBy;
    }
})();

// Add tab to the URL because on initial load it probably won't be there
setQueryParameters({ tab: initialTab });

// Fire page tracking event
trackPageCall({ cmp: getQueryParameter('cmp') });

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
}

export default function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        const handleResize = throttle(() => {
            setWindowDimensions(getWindowDimensions());
        }, 100);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
}

enum SidebarVisibility {
    VISIBLE_DESKTOP = 'visibleDesktop',
    VISIBLE_MOBILE_DESKTOP = 'visibleMobileDesktop',
    HIDDEN_MOBILE_DESKTOP = 'hiddenMobileDesktop',
}

const AppContext = createContext({
    searchQuery: '',
    currentTab: '',
    setCurrentTab: null,
    facetsLoaded: false,
    setFacetsLoaded: null,
    initialRenderComplete: false,
    onResultsLoaded: null,
    resultsDone: false,
});
const HitsContext = createContext({ tab: '', topPosition: 1 });

const App: React.FunctionComponent = () => {
    const [searchState, setSearchState] = useState<SearchState>({});
    const [facetsLoaded, setFacetsLoaded] = useState<boolean>(false);
    const [initialRenderComplete, setInitialRenderComplete] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<string>(initialTab);
    const [initialFiltersApplied, setInitialFiltersApplied] = useState<boolean>(false);
    const [resultsDone, setResultsDone] = useState<boolean>(false);

    const onSearchStateChange = (updatedSearchState: SearchState) => {
        const { configure, page, sortBy, ...updatedFilters } = updatedSearchState;
        if (facetsLoaded) {
            updateFiltersInQueryString(updatedFilters);
            setQueryParameters({ p: page > 1 ? page : null });
            setQueryParameters({ sortBy: sortBy ? sortBy : '' });
            setSearchState(updatedSearchState);
        }
    };

    const onResultsLoaded = () => {
        // If there were filters sent through the URL we don't want to setResultsDone
        // until the initial results are cleared out and the filtered results have
        // been received -- true condition will only fire once
        if (!resultsDone && initialFiltersApplied) {
            // Unfortunately our onResultsLoaded callback isn't perfect. Occasionally
            // Algolia will report that search is complete before rendering of new results
            // is complete, and the user sees a flash of the unfiltered results. We add a
            // moment before we display the results to allow Algolia to catch up.
            setTimeout(() => {
                setResultsDone(true);
            }, 500);
        }
    };

    useEffect(() => {
        // We need to load unfiltered results first so we have a cache of grayed out filters,
        // then apply the filters sent through the URL -- true condition will only fire once
        if (!initialRenderComplete && facetsLoaded) {
            const initialFilters = getFiltersFromQueryString();
            if (initialFilters || initialPage || initialSortBy) {
                let newSearchState = { ...searchState };
                if (initialFilters) {
                    updateFiltersInQueryString(initialFilters);
                    newSearchState = { ...newSearchState, ...initialFilters };
                }
                if (initialPage) {
                    setQueryParameters({ p: initialPage });
                    newSearchState = { ...newSearchState, ...{ page: initialPage } };
                }
                if (initialSortBy) {
                    setQueryParameters({ sortBy: initialSortBy });
                    newSearchState = { ...newSearchState, ...{ sortBy: initialSortBy } };
                }
                setSearchState({ ...newSearchState, ...initialFilters });
                setInitialFiltersApplied(true);
            } else {
                setResultsDone(true);
            }
            setInitialRenderComplete(true);
        }
    }, [facetsLoaded]);

    useEffect(() => {
        if (initialRenderComplete) {
            setNewTabInQueryString(currentTab);
        }
    }, [currentTab]);

    return (
        <div className="searchApp">
            <AppContext.Provider
                value={{
                    searchQuery: searchQuery,
                    currentTab: currentTab,
                    setCurrentTab: setCurrentTab,
                    facetsLoaded: facetsLoaded,
                    setFacetsLoaded: setFacetsLoaded,
                    initialRenderComplete: initialRenderComplete,
                    onResultsLoaded: onResultsLoaded,
                    resultsDone: resultsDone,
                }}
            >
                <InstantSearch
                    searchClient={algoliasearch(SiteConfig.algoliaAppId, SiteConfig.algoliaApiKey)}
                    indexName={SiteConfig.algoliaIndex}
                    onSearchStateChange={onSearchStateChange}
                    searchState={searchState}
                >
                    <Configure
                        clickAnalytics={true}
                        hitsPerPage={51}
                        filters={(() => {
                            const tabConfig = TabConfigByName[currentTab];
                            return tabConfig?.filterQuery || null;
                        })()}
                    />
                    <SearchBox defaultRefinement={searchQuery} />
                    <Results />
                </InstantSearch>
            </AppContext.Provider>
        </div>
    );
};

// Leave this empty -- we only use it to set a defaultRefinement
const SearchBoxComponent = () => <></>;
const SearchBox = connectSearchBox(SearchBoxComponent);

const ResultsComponent = ({ searchResults, error, searching }: any) => {
    const appContext = useContext(AppContext);

    const [resultsLoaded, setResultsLoaded] = useState<boolean>(false);

    const { height, width } = useWindowDimensions();
    const isDesktop = () => width >= 1024;

    const [sidebarVisibility, setSidebarVisibility] = useState<SidebarVisibility>(
        isDesktop() ? SidebarVisibility.VISIBLE_DESKTOP : SidebarVisibility.HIDDEN_MOBILE_DESKTOP,
    );
    const sidebarIsHidden = () => sidebarVisibility === SidebarVisibility.HIDDEN_MOBILE_DESKTOP;

    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const getFilterButtonLabel = () => {
        return isDesktop()
            ? sidebarVisibility === SidebarVisibility.VISIBLE_MOBILE_DESKTOP ||
              sidebarVisibility === SidebarVisibility.VISIBLE_DESKTOP
                ? 'Hide filters'
                : 'Show filters'
            : sidebarVisibility === SidebarVisibility.VISIBLE_MOBILE_DESKTOP
            ? 'Hide filters'
            : 'Show filters';
    };

    // Allow for scroll-up on pagination clicks
    const hitsRef = useRef<HTMLDivElement>(null);
    const [offsetTop, setOffsetTop] = useState<number>();
    useEffect(() => {
        setOffsetTop(hitsRef.current?.offsetTop);
    }, [hitsRef.current?.offsetTop]);

    if (searchQuery && searchResults) {
        if (searchResults.facets.length || searchResults.disjunctiveFacets.length) {
            // Once we have facets we know the grayed filter caching will be performed.
            // Make this async so it doesn't interfere with search results rendering.
            setTimeout(() => {
                if (!appContext.facetsLoaded) {
                    appContext.setFacetsLoaded(true);
                }
            }, 1);
        }

        if (resultsLoaded !== !searching) {
            // Algolia doesn't provide a callback for when requested search results
            // have been returned, so we'll create our own. Make this async so it
            // doesn't interfere with search results rendering.
            setResultsLoaded(!searching);
            if (!searching) {
                setTimeout(() => {
                    appContext.onResultsLoaded();
                }, 1);
            }
        }

        // Initialize focus trapping inside of filter sidebar in mobile/tablet
        let tabFocusHandle, whenKeyHandle;
        const tabFocusOptions = { context: '.searchApp .visibleMobileDesktop .sidebar' };
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const whenKeyOptions = { escape: () => hideFilters() };
        const showFilters = () => {
            if (isDesktop()) {
                tabFocusHandle?.disengage();
                tabFocusHandle = null;
                whenKeyHandle?.disengage();
                whenKeyHandle = null;
                setSidebarVisibility(SidebarVisibility.VISIBLE_DESKTOP);
            } else {
                setSidebarVisibility(SidebarVisibility.VISIBLE_MOBILE_DESKTOP);
                tabFocusHandle = allyTabFocus(tabFocusOptions);
                whenKeyHandle = allyWhenKey(whenKeyOptions);
            }
        };
        const hideFilters = () => {
            tabFocusHandle?.disengage();
            tabFocusHandle = null;
            whenKeyHandle?.disengage();
            whenKeyHandle = null;
            filterButtonRef.current.focus();
            setSidebarVisibility(SidebarVisibility.HIDDEN_MOBILE_DESKTOP);
        };

        return (
            <>
                <div className="gridFreeContainer">
                    <h2 className="searchResultsForMsg">Results for &ldquo;{searchQuery}&rdquo;</h2>
                </div>
                <div className="searchMenu">
                    <div className="gridFreeContainer">
                        <Navigation
                            setCurrentTab={appContext.setCurrentTab}
                            tabs={TabConfig.map((tab: any) => ({ label: tab.label, name: tab.name }))}
                            initialTab={initialTab}
                        />
                    </div>
                </div>
                <div className="gridFreeContainer">
                    <div className="searchHeader">
                        <div className="searchHeaderRow">
                            <div className="searchStats">
                                <Stats />
                            </div>

                            <div className="rightToolbar">
                                <div className="rightToolbarTools">
                                    <button
                                        className="filtersBtn"
                                        ref={filterButtonRef}
                                        onClick={() => {
                                            sidebarIsHidden() ? showFilters() : hideFilters();
                                        }}
                                    >
                                        <Label semibold>{getFilterButtonLabel()}</Label>
                                        <Filter />
                                    </button>

                                    <SortBy
                                        type="dropdown"
                                        defaultRefinement={SiteConfig.algoliaIndex}
                                        items={(() => {
                                            const tabConfig = TabConfigByName[appContext.currentTab];
                                            return tabConfig?.sorts || [];
                                        })()}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="searchHeaderRow">
                            <div className="vatInfo">
                                <p>
                                    Prices shown do not include taxes and duty fees.{' '}
                                    <a
                                        href="https://help.sothebys.com/en/support/solutions/articles/44002430313-what-taxes-do-i-pay-on-my-purchase-"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={'Prices shown do not include taxes and duty fees. Read More'}
                                    >
                                        Read More
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <main>
                    <div className={`gridContainer container ${sidebarVisibility}`}>
                        <div className="gridSidebar sidebar">
                            <button className="closeWrapper" onClick={hideFilters}>
                                <Close />
                            </button>
                            <Filters
                                tab={appContext.currentTab}
                                filters={(() => {
                                    const tabConfig = TabConfigByName[appContext.currentTab];
                                    return tabConfig?.subFilterList || [];
                                })()}
                            />
                        </div>
                        <div className="sideBarBackdrop" onClick={hideFilters}></div>
                        <div
                            className={`searchAppGridContent ${appContext.resultsDone ? '' : 'searchLoading'}`}
                            ref={hitsRef}
                        >
                            {!appContext.resultsDone && (
                                <div className="searchLoadingOverlay">{loadingIcon('black')}</div>
                            )}
                            <HitsContext.Provider value={{ tab: appContext.currentTab, topPosition: offsetTop }}>
                                {!!searchResults.hits.length && <Hits />}

                                {!searchResults.hits.length && (
                                    <div className="gridFreeContainer">
                                        <div className="searchNoResultsMsg">
                                            <div>Sorry, there are no results. Please try modifying your search.</div>
                                            <ClearRefinements />
                                        </div>
                                    </div>
                                )}

                                <Pagination tab={appContext.currentTab} />
                            </HitsContext.Provider>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    if (!searchQuery) {
        return (
            <div className="gridFreeContainer">
                <h2 className="searchResultsForMsg">No search query provided</h2>
            </div>
        );
    }

    return null;
};
const Results = connectStateResults(ResultsComponent);

export { App, AppContext, HitsContext };
